import { VerticalAlignTop } from '@mui/icons-material';
import moment from 'moment';
import Tooltip from '@mui/material/Tooltip';
const validateNumber = (value, type, digits = 2) => {
    value = value.toString();
    // Handle decimal point at start by adding leading zero
    if (value && value.toString().startsWith('.')) {
        value = '0' + value;
    }

    const num = Number(value);
    if (type === 'integer' && Number.isInteger(num) && num >= 0) {
        return true;
    }

    if (type === 'number') {
        if (num >= 0) return true;
        else return false;
    }

    if (type === 'percent') {
        if (num >= 0 && num <= 100) return true;
        else return false;
    }

    if (type === 'decimal') {
        // Check if it's a valid number
        if (isNaN(num) || num < 0) {
            return false;
        }
        // Check if it has more than 2 decimal places
        const decimalPlaces = (value.toString().split('.')[1] || '').length;
        return decimalPlaces <= digits;
    }

    return type === 'string';
};

const validateAlphanumeric = (string) => {
    return /^[a-zA-Z0-9\s]*$/.test(string);
};

// Usernames allow underscore, dot and hyphen in addition to alphanumerics.
// SSO-provisioned users get names like "sso_13214", which must be editable.
const validateUsername = (string) => {
    return /^[a-zA-Z0-9_.\- ]*$/.test(string);
};

const validateAlphaPlus = (string) => {
    return /^[\.a-zA-Z0-9,!?' -]*$/.test(string);
};

const validateInputValue = (value, type) => {
    const x = parseFloat(value);
    if (type === 'percentage') {
        return !(isNaN(x) || x < 0 || x > 100);
    } else if (type === 'minutes') {
        return !(isNaN(x) || x < 0 || x > 60);
    } else if (type === 'hours') {
        return !(isNaN(x) || x < 0 || x > 24);
    } else {
        return !(isNaN(x) || x < 0 || x > 255);
    }
};

const validateEmail = (email) => {
    return email.match(
        /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    );
};

const validatePassword = (
    password,
    min = 8,
    max = 20,
    isCap = true,
    isLwr = true,
    isNum = true,
    isSpc = '!@#$%^&*()_+-={}[]|\\:;<>.?/'
) => {
    const errors = [];

    if (password.length < min || password.length > max) {
        errors.push(`Password must be between ${min} and ${max} characters`);
    }

    if (isNum && !/\d/.test(password)) {
        errors.push('Must contain at least one number');
    }

    if (isLwr && !/[a-z]/.test(password)) {
        errors.push('Must contain at least one lowercase letter');
    }

    if (isCap && !/[A-Z]/.test(password)) {
        errors.push('Must contain at least one uppercase letter');
    }

    if (isSpc) {
        // Escape regex characters in the special character string
        const escapedSpecials = isSpc.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
        const specialRegex = new RegExp(`[${escapedSpecials}]`);
        if (!specialRegex.test(password)) {
            errors.push(`Must contain at least one special character: ${isSpc}`);
        }
    }

    return {
        isValid: errors.length === 0,
        errors
    };
};

//const validateDate = (theDate, format = 'YYYY-MM-DD') => {
//    return moment(theDate, format, true).isValid();
//};

const validateCustomDateRange = (startDate, endDate, timeInterval) => {
    const start = moment(convertDate(startDate));
    const end = moment(convertDate(endDate));
    const diffDays = end.diff(start, 'days');

    switch (timeInterval) {
        case 'hour':
            if (diffDays > 30) {
                return {
                    isValid: false,
                    message: 'For hourly intervals, date range cannot exceed a month'
                };
            }
            break;
        case 'day':
            if (diffDays > 180) {
                return {
                    isValid: false,
                    message: 'For daily intervals, date range cannot exceed 6 months'
                };
            }
            break;
    }

    return {
        isValid: true,
        message: ''
    };
};

const applyColumnStyle = (columns) =>
    columns.map((c) => {
        switch (c.headerName) {
            case 'Group':
                return {
                    ...c,
                    sortable: true,
                    resizable: true,
                    headerClassName: 'device-column-header',
                    cellClassName: 'wrapCell',
                    renderCell: renderGroupCell
                };
            case 'Policy':
                return {
                    ...c,
                    sortable: true,
                    resizable: true,
                    headerClassName: 'device-column-header',
                    cellClassName: 'wrapCell',
                    renderCell: renderPolicyCell
                };
            case 'Client Version':
                return {
                    ...c,
                    sortable: true,
                    resizable: true,
                    headerClassName: 'device-column-header',
                    cellClassName: 'wrapCell',
                    renderCell: renderClientCell
                };
            case 'Licensed':
                return {
                    ...c,
                    sortable: true,
                    resizable: true,
                    headerClassName: 'device-column-header',
                    cellClassName: 'wrapCell',
                    renderCell: renderLicenseCell
                };
            default:
                return {
                    ...c,
                    sortable: true,
                    resizable: true,
                    headerClassName: 'device-column-header',
                    cellClassName: 'wrapCell'
                };
        }
    });

const renderGroupCell = (params) => {
    const { value, row } = params;

    if (row.g_id > 0 && row.adhocgroup === 1) {
        return (
            <Tooltip title="Manually assigned" placement="right" arrow>
                <span className="inline-block cursor-pointer">
                    {value} <span style={{ color: '#00a2e8', fontWeight: 'bold', position: 'relative', bottom: '8px' }}>M</span>
                </span>
            </Tooltip>
        );
    }
    return value;
};

const renderPolicyCell = (params) => {
    const { value, row } = params;

    if (row.p_id > 0 && row.adhocpolicy === 1) {
        return (
            <Tooltip title="Manually assigned" placement="right" arrow>
                <span className="inline-block cursor-pointer">
                    {value} <span style={{ color: '#00a2e8', fontWeight: 'bold', position: 'relative', bottom: '8px' }}>M</span>
                </span>
            </Tooltip>
        );
    }
    return value;
};

const renderLicenseCell = (params) => {
    const { value, row } = params;
    if (row.islicensed == 'False' && row.adhoclicense) {
        return (
            <Tooltip title="Licensing disabled" placement="right" arrow>
                <span className="inline-block cursor-pointer">
                    {value} <span style={{ color: '#ed1c24', fontWeight: 'bold', position: 'relative', bottom: '8px' }}>D</span>
                </span>
            </Tooltip>
        );
    }
    return value;
};

const renderClientCell = (params) => {
    const { value, row } = params;
    if (row.upgrade == 0 && row.adhocupgrade) {
        return (
            <Tooltip title="Upgrade disabled" placement="right" arrow>
                <span className="inline-block cursor-pointer">
                    {value} <span style={{ color: '#ed1c24', fontWeight: 'bold', position: 'relative', bottom: '8px' }}>D</span>
                </span>
            </Tooltip>
        );
    }
    return value;
};

const handleDeviceColumns = (data) => {
    const columns_order = [];
    const columns_visibility = {};
    for (let i = 0; i < data.length; i++) {
        let columnWidth = 170;
        if (
            data[i].column_name === 'os_ver' ||
            data[i].column_name === 'os_name' ||
            data[i].column_name === 'disp_1_nm' ||
            data[i].column_name === 'disp_2_nm' ||
            data[i].column_name === 'sys_type' ||
            data[i].column_name === 'manufacturer' ||
            data[i].column_name === 'version' ||
            data[i].column_name === 'username' ||
            data[i].column_name === 'islicensed' ||
            data[i].column_name === 'isportable' ||
            data[i].column_name === 'groupid_id'
        ) {
            columnWidth = 100;
        }
        if (data[i].column_visibility === true) {
            columns_order.push({
                field: data[i].column_name,
                headerName: data[i].header_name,
                sortable: true,
                resizable: true,
                headerClassName: 'device-column-header',
                cellClassName: 'wrapCell',
                width: columnWidth
            });
        }
        columns_visibility[data[i].column_name] = data[i].column_visibility;
    }
    return [columns_order, columns_visibility];
};

const handleUpdateReportDateRange = (value) => {
    const today = new Date();
    let startDate;
    if (value === '1week') {
        startDate = today.setDate(today.getDate() - 7);
    } else if (value === '2weeks') {
        startDate = today.setDate(today.getDate() - 14);
    } else if (value === '1month') {
        startDate = today.setMonth(today.getMonth() - 1);
    } else if (value === '3months') {
        startDate = today.setMonth(today.getMonth() - 3);
    } else if (value === '6months') {
        startDate = today.setMonth(today.getMonth() - 6);
    } else if (value === '1year') {
        startDate = today.setFullYear(today.getFullYear() - 1);
    } else {
        startDate = today;
    }
    return startDate;
};

const convertDate = (value) => {
    if (value.$d) {
        return moment(value.$d).format('YYYY-MM-DD');
    } else {
        return moment(value).format('YYYY-MM-DD');
    }
};

const capitalizeFirstLetter = (word) => {
    if (!word) return '';
    return word.charAt(0).toUpperCase() + word.slice(1);
};

export {
    validateNumber,
    validateInputValue,
    validateAlphaPlus,
    validateAlphanumeric,
    validateUsername,
    validateEmail,
    validatePassword,
    //validateDate,
    validateCustomDateRange,
    handleDeviceColumns,
    handleUpdateReportDateRange,
    convertDate,
    applyColumnStyle,
    capitalizeFirstLetter
};
